'use es6';

import { deref, watch, unwatch } from 'atom';
import debounce from 'transmute/debounce';
import forEach from 'transmute/forEach';
import get from 'transmute/get';
import omit from 'transmute/omit';
import { Component, forwardRef } from 'react';
import UISelect from 'UIComponents/input/UISelect';
import { isResolved, isError } from 'reference-resolvers/utils';
import defaultFilterOptions from 'customer-data-reference-ui-components/defaults/defaultFilterOptions';
import { defaultEnumOptionFormatter } from '../defaults/defaultEnumOptionFormatter';
import { jsx as _jsx } from "react/jsx-runtime";
const propertiesToOmit = ['open', 'defaultOpen', 'onOpenChange', 'onSecondaryChange', 'onCancel', 'requestOptions', 'optionFormatter', 'minimumSearchLength', 'searchCount', 'onTracking', 'innerRef'];
const unwatchAll = forEach(unwatcher => unwatcher());
function getUniqueOptions(options) {
  const hasValue = {};
  return options.filter(({
    value,
    options: nestedOptions
  }) => {
    /*
      We never de-dupe grouped options.
      Search does not currently return grouped options, but if it ever does, multiple groups
      with the same name and contents may appear. Grouped options passed in the `options` prop
      will not be de-duped with search results, allowing the same value to be used by multiple
      options in the select (once in the custom `options` group and again in the search
      results in the root)
    */
    if (nestedOptions) {
      return true;
    }
    if (hasValue[value]) {
      return false;
    }
    hasValue[value] = true;
    return true;
  });
}
const defaultFormatter = record => Object.assign({}, defaultEnumOptionFormatter(record), {
  record
});
const defaultProps = {
  autoload: true,
  minimumSearchLength: 0,
  optionFormatter: defaultFormatter,
  options: [],
  readOnly: false,
  searchCount: 10,
  searchDebounceTimeout: 300,
  showTags: true,
  filterOptions: defaultFilterOptions,
  defaultOpen: false
};
export class ReferenceInputEnumSearch extends Component {
  constructor(props) {
    super();
    this.handleInputChange = ({
      target: {
        value
      }
    }) => {
      this.setState({
        value
      });
    };
    this.handleLoadOptionsSearch = (search, loadOptions) => {
      // don't load options until the dropdown is showing
      if (!this.isOpen()) {
        return;
      }
      const {
        minimumSearchLength,
        optionFormatter,
        requestOptions,
        resolver,
        searchCount
      } = this.props;
      if (search.length < minimumSearchLength) {
        const options = getUniqueOptions(this.getOptions());
        loadOptions(null, {
          options,
          pagination: {
            hasMore: false,
            length: options.length
          }
        });
        return;
      }
      if (!this.searchHasMore &&
      // don't delegate to local search if filterOptions is false, as it
      // won't properly narrow down results
      this.props.filterOptions !== false && search.indexOf(this.prevSearchValue) === 0 && this.prevSearchResult) {
        loadOptions(null, this.createSearchResult());
        return;
      }
      const searchId = this.nextSearchId();
      const searchAtom = resolver.search(Object.assign({
        query: search,
        count: searchCount,
        offset: this.prevSearchValue === search && this.prevSearchResult && this.prevSearchResult.pagination.searchOffset || 0,
        nextCursor: this.prevSearchValue === search && this.prevSearchResult && this.prevSearchResult.pagination.nextCursor || null
      }, requestOptions));
      const searchHandler = response => {
        if (isError(response)) {
          this.unwatchSearchAtom(searchId);
          loadOptions(null, this.prevSearchResult ? this.createSearchResult() : null);
          return;
        }
        if (!isResolved(response)) {
          return;
        }
        const hasMore = get('hasMore', response);
        const offset = get('offset', response);
        const results = get('results', response);
        const total = get('total', response);
        // support for cursor pagination; see SlackAPI for example
        const nextCursor = get('nextCursor', response);
        const prevOptions = this.prevSearchValue === search && this.prevSearchResult && this.prevSearchResult.options || [];
        const options = getUniqueOptions(prevOptions.concat(results.map(optionFormatter).toArray()));
        this.searchHasMore = hasMore;
        this.prevSearchValue = search;
        this.prevSearchResult = {
          options,
          pagination: {
            hasMore,
            length: total,
            offset: total ? total - options.length : undefined,
            searchOffset: offset,
            nextCursor
          }
        };
        this.unwatchSearchAtom(searchId);
        loadOptions(null, this.createSearchResult());
      };
      const currentResult = deref(searchAtom);
      if (isResolved(currentResult)) {
        searchHandler(currentResult);
      } else {
        this.watchSearchAtom(searchId, searchAtom, searchHandler);
      }
    };
    this.handleLoadOptions = (search, loadOptions) => {
      if (search) {
        this.handleLoadOptionsSearchDebounced(search, loadOptions);
      } else {
        this.handleLoadOptionsSearch(search, loadOptions);
      }
    };
    this.handleOpenChange = evt => {
      const {
        onOpenChange
      } = this.props;
      const {
        value
      } = evt.target;
      this.setState({
        open: value
      });
      if (onOpenChange) {
        onOpenChange(evt);
      }
    };
    this.handleValueOptionReferenceResolved = reference => {
      const {
        optionFormatter
      } = this.props;
      if (isResolved(reference)) {
        this.setState(prevState => ({
          valueOptions: prevState.valueOptions.concat(optionFormatter(reference))
        }));
      }
    };
    this.handleLoadOptionsSearchDebounced = debounce(props.searchDebounceTimeout, this.handleLoadOptionsSearch);
    this.prevSearchResult = null;
    this.prevSearchValue = '';
    this.unwatchSearchAtoms = {};
    this.searchHasMore = true;
    this.watchedReferenceAtoms = [];
    this.searchId = 0;
    this.state = {
      open: props.defaultOpen,
      valueOptions: []
    };
  }
  componentDidMount() {
    const {
      resolver,
      value,
      options
    } = this.props;
    this.loadCurrentValueOptions(resolver, value, options);
  }
  componentDidUpdate(prevProps) {
    const {
      resolver,
      value,
      options
    } = this.props;
    if (value !== prevProps.value) {
      this.loadCurrentValueOptions(resolver, value, options);
    }
  }
  componentWillUnmount() {
    this.unwatchAllSearchAtoms();
    this.watchedReferenceAtoms.forEach(referenceAtom => {
      unwatch(referenceAtom, this.handleValueOptionReferenceResolved);
    });
  }
  nextSearchId() {
    return String(++this.searchId);
  }
  getOptions(extraOptions = []) {
    const {
      options
    } = this.props;
    const {
      valueOptions
    } = this.state;
    return options.concat(valueOptions, extraOptions);
  }
  isOpen() {
    if (this.props.open !== undefined) {
      return this.props.open;
    }
    return this.state.open;
  }
  createSearchResult() {
    // Appends search results to client-side options
    return Object.assign({}, this.prevSearchResult, {
      options: getUniqueOptions(this.getOptions(this.prevSearchResult.options))
    });
  }
  watchSearchAtom(id, searchAtom, searchHandler) {
    this.unwatchSearchAtoms[id] = () => unwatch(searchAtom, searchHandler);
    watch(searchAtom, searchHandler);
  }
  unwatchSearchAtom(id) {
    if (!this.unwatchSearchAtoms[id]) {
      return;
    }
    this.unwatchSearchAtoms[id]();
    delete this.unwatchSearchAtoms[id];
  }
  unwatchAllSearchAtoms() {
    unwatchAll(this.unwatchSearchAtoms);
    this.unwatchSearchAtoms = {};
  }
  loadCurrentValueOptions(resolver, value, options = []) {
    if (!value) {
      return;
    }
    const optionValues = options.map(option => option.value);
    const arrayValue = Array.isArray(value) ? value : [value];
    this.watchedReferenceAtoms.forEach(reference => {
      unwatch(reference, this.handleValueOptionReferenceResolved);
    });
    this.watchedReferenceAtoms = arrayValue.filter(id => optionValues.indexOf(id) === -1).map(id => resolver.byId(id));
    this.watchedReferenceAtoms.forEach(reference => {
      watch(reference, this.handleValueOptionReferenceResolved);
      this.handleValueOptionReferenceResolved(deref(reference));
    });
  }
  getButtonContent() {
    if (!this.props.showTags) {
      return this.props.ButtonContent;
    }
    return null;
  }
  render() {
    const {
      value,
      placeholder,
      filterOptions
    } = this.props;
    const transferrableProps = omit(propertiesToOmit, this.props);
    const SelectComponent = this.props.SelectComponent || UISelect;
    return /*#__PURE__*/_jsx(SelectComponent, Object.assign({}, transferrableProps, {
      anchorType: "button",
      cache: null,
      filterOptions: filterOptions,
      loadOptions: this.handleLoadOptions,
      onInputValueChange: this.handleInputChange,
      onOpenChange: this.handleOpenChange,
      ButtonContent: this.getButtonContent(),
      inputRef: this.props.inputRef,
      open: this.isOpen(),
      options: this.getOptions(),
      placeholder: placeholder,
      ref: this.props.innerRef,
      value: value,
      menuWidth: this.props.menuWidth
    }));
  }
}
ReferenceInputEnumSearch.defaultProps = defaultProps;
export default /*#__PURE__*/forwardRef((props, ref) => /*#__PURE__*/_jsx(ReferenceInputEnumSearch, Object.assign({}, props, {
  innerRef: ref
})));